import {
  afterRenderEffect,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatListItem, MatListItemIcon, MatNavList } from '@angular/material/list';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FooterModule } from '@psu-pennstateit-swe/components/layout';
import { ExternalLinkDirective } from '@psu-pennstateit-swe/utils/angular';
import { CdnPipe } from '@psu-pennstateit-swe/utils/cdn';
import { ScreenSizeDirective, ScreenSizeService } from '@psu-pennstateit-swe/utils/responsive';
import { filter } from 'rxjs';
import { APP_CONFIG } from './app-properties.model';
import { NavigationItems } from './app.routes';

/** Represents the three user-selectable theme modes: explicit light, explicit dark, or system-driven auto. */
export type ThemeMode = 'light' | 'dark' | 'auto';

@Component({
  selector: 'ssrm-root',
  imports: [
    RouterOutlet,
    MatToolbar,
    MatButton,
    MatIconButton,
    CdnPipe,
    ExternalLinkDirective,
    FooterModule,
    ScreenSizeDirective,
    MatIcon,
    MatMenuItem,
    MatSidenavContent,
    MatMenu,
    MatTooltip,
    MatMenuTrigger,
    MatListItem,
    MatListItemIcon,
    RouterLink,
    RouterLinkActive,
    MatNavList,
    MatSidenav,
    MatSidenavContainer,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  readonly skipLinkElement = viewChild.required<ElementRef<HTMLAnchorElement>>('skipLink');
  readonly mainContentAreaElement = viewChild.required<ElementRef<HTMLElement>>('mainContentArea');
  protected readonly navigationItems = Object.entries(NavigationItems)
    .filter(([, item]) => !item.featureFlag || inject(APP_CONFIG).featureFlags[item.featureFlag.name])
    .map(([path, item]) => ({
      path,
      ...item,
    }));

  /** Logo variants — the dark-theme logo uses reversed (white) colors for contrast on dark backgrounds. */
  readonly lightThemeLogoPath = 'img/PSU_HOR_RGB_2C.svg';
  readonly darkThemeLogoPath = 'img/PSU_HOR_REV_RGB_2C.svg';

  /** The currently selected theme mode; defaults to 'auto' so new users follow their OS preference. */
  readonly themeMode = signal<ThemeMode>('auto');
  /** Tracks the OS-level dark-mode media query result, updated via a `change` event listener. */
  private readonly systemPrefersDark = signal(false);

  /**
   * Resolves whether dark theme is currently active.
   * When mode is 'auto', defers to the OS preference; otherwise uses the explicit selection.
   */
  readonly isDarkThemeActive = computed(() => {
    const mode = this.themeMode();
    if (mode === 'auto') {
      return this.systemPrefersDark();
    }
    return mode === 'dark';
  });

  /** Maps the active theme mode to its icon name and display label */
  private readonly themeDisplay = computed(() => {
    switch (this.themeMode()) {
      case 'light':
        return { icon: 'light_mode', label: 'Light' };
      case 'dark':
        return { icon: 'dark_mode', label: 'Dark' };
      case 'auto':
        return { icon: 'brightness_auto', label: 'Auto' };
    }
  });

  /** Toolbar icon name for the current theme mode, derived from `themeDisplay`. */
  readonly themeIcon = computed(() => this.themeDisplay().icon);
  /** Display label for the current theme mode, derived from `themeDisplay`. */
  readonly themeLabel = computed(() => this.themeDisplay().label);

  /** Switches the logo between light and dark variants whenever the effective theme changes. */
  readonly logoPath = computed(() => (this.isDarkThemeActive() ? this.darkThemeLogoPath : this.lightThemeLogoPath));
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly navigationEnd = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)));
  readonly isLandingPage = computed(
    () => (this.navigationEnd()?.urlAfterRedirects ?? this.router.url).split(/[?#]/, 1)[0] === '/',
  );
  readonly isMobileLayout = computed(() => this.screenSizeService.isHandset() && this.screenSizeService.isPortrait());
  private hasCompletedInitialNavigation = this.router.navigated;
  /** localStorage key used to persist the user's theme preference across sessions. */
  private readonly themeStorageKey = 'ssrm-theme-mode';

  constructor() {
    this.matIconRegistry.addSvgIconSet(this.domSanitizer.bypassSecurityTrustResourceUrl('mdi.svg'));

    this.initializeTheme();

    effect(() => {
      this.applyTheme(this.isDarkThemeActive());
    });

    afterRenderEffect({
      write: () => {
        const navigationEvent = this.navigationEnd();
        if (!navigationEvent) {
          return;
        }
        if (!this.hasCompletedInitialNavigation) {
          this.hasCompletedInitialNavigation = true;
          return;
        }
        untracked(() => {
          this.focusPageAfterNavigation();
        });
      },
    });
  }

  /** Updates the active theme mode and persists the choice; the effect in the constructor applies the DOM change. */
  setTheme(mode: ThemeMode) {
    this.themeMode.set(mode);
    this.persistThemePreference(mode);
  }

  skipToMain(event: MouseEvent) {
    if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.focusMainContent();
  }

  private focusMainContent() {
    const main = this.mainContentAreaElement().nativeElement;
    const heading = main.querySelector<HTMLHeadingElement>('h1');
    if (heading) {
      heading.tabIndex = -1;
    }
    (heading ?? main).focus();
  }

  private focusPageAfterNavigation() {
    const main = this.mainContentAreaElement().nativeElement;
    const focusedElement = this.document.activeElement;
    // Retained controls (e.g. Relationships tabs) keep focus; main itself is a route fallback.
    if (focusedElement !== main && main.contains(focusedElement)) {
      return;
    }
    this.focusMainContent();
  }

  /**
   * Bootstraps the theme system on app load:
   * 1. Registers the OS dark-mode media query and keeps `systemPrefersDark` in sync.
   * 2. Restores any previously persisted preference, falling back to 'auto'.
   * 3. Immediately applies the resolved theme so there is no flash of wrong theme.
   */
  private initializeTheme() {
    const mediaQuery = this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)');
    if (mediaQuery) {
      this.systemPrefersDark.set(mediaQuery.matches);
      mediaQuery.addEventListener('change', (e) => {
        this.systemPrefersDark.set(e.matches);
      });
    }

    const storedPreference = this.getStoredThemePreference();
    this.themeMode.set(storedPreference ?? 'auto');
  }

  /**
   * Applies the theme by toggling the `dark` attribute on `<html>`.
   * The Material theme and global CSS target this attribute to switch color tokens.
   */
  private applyTheme(isDark: boolean) {
    if (isDark) {
      this.document.documentElement.setAttribute('dark', '');
      return;
    }
    this.document.documentElement.removeAttribute('dark');
  }

  /** Reads and validates the stored theme preference, returning null for unrecognized values. */
  private getStoredThemePreference(): ThemeMode | null {
    const preference = this.document.defaultView?.localStorage.getItem(this.themeStorageKey);
    if (preference === 'light' || preference === 'dark' || preference === 'auto') {
      return preference;
    }
    return null;
  }

  /** Writes the selected mode to localStorage so it survives page refreshes. */
  private persistThemePreference(mode: ThemeMode) {
    this.document.defaultView?.localStorage.setItem(this.themeStorageKey, mode);
  }
}
